"use client";

import React, { createContext, useContext } from "react";
import toast, { Toaster } from "react-hot-toast";

interface ToastContextType {
  showToast: (message: string, type?: "success" | "error" | "info" | "warning", duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const showToast = (
    message: string,
    type: "success" | "error" | "info" | "warning" = "success",
    duration = 4000
  ) => {
    switch (type) {
      case "success":
        toast.success(message, { duration });
        break;
      case "error":
        toast.error(message, { duration });
        break;
      case "info":
        toast(message, { duration });
        break;
      case "warning":
        toast(message, { duration, icon: "⚠️" });
        break;
      default:
        toast(message, { duration });
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toaster
        position="bottom-right"
        gutter={10}
        toastOptions={{
          duration: 4000,
          style: {
            background: "#fff",
            color: "#1c1917",
            borderRadius: "12px",
            border: "1px solid #e7e5e4",
            boxShadow: "0 10px 40px -8px rgba(0,0,0,0.15)",
            fontSize: "14px",
            fontWeight: 500,
            padding: "12px 16px",
            maxWidth: "360px",
          },
          success: {
            iconTheme: { primary: "#f59e0b", secondary: "#fff" },
          },
          error: {
            iconTheme: { primary: "#ef4444", secondary: "#fff" },
          },
        }}
      />
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
